// Iter 30x: combobox per selezionare cliente da anagrafica esistente o crearlo
// al volo dal form Commessa. Sposta l'attrito di "ti tocca andare prima nei
// Clienti" → l'utente compila la commessa in un solo passaggio.
import { useEffect, useMemo, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Plus, Check, Sparkles, Loader2, Building2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function ClienteCombobox({
  value,         // testo cliente attualmente selezionato
  onChange,      // (cliente_nome: string) => void  — chiamato sia su select sia su typing
  onClienteCreated, // optional callback dopo creazione, riceve il documento
  clienti = [],  // lista di clienti da mostrare (passata dal parent o caricata internally)
  testId = "cliente-combobox",
  placeholder = "Cliente — cerca o crea nuovo",
  defaultStabilimento = "", // Iter 46: codice EG-XX suggerito dal contesto (es. dal codice commessa)
}) {
  const [text, setText] = useState(value || "");
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapperRef = useRef(null);
  // Carica clienti internamente se non passati
  const [localClienti, setLocalClienti] = useState(clienti);

  useEffect(() => { setText(value || ""); }, [value]);

  useEffect(() => {
    if (clienti && clienti.length > 0) {
      setLocalClienti(clienti);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/clienti");
        if (!cancelled) setLocalClienti(Array.isArray(data) ? data : []);
      } catch (_) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [clienti]);

  useEffect(() => {
    function onDocClick(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const filtered = useMemo(() => {
    const q = (text || "").trim().toLowerCase();
    if (!q) return localClienti.slice(0, 12);
    return localClienti
      .filter(
        (c) =>
          (c.nome || "").toLowerCase().includes(q) ||
          (c.partita_iva || "").toLowerCase().includes(q) ||
          (c.citta || "").toLowerCase().includes(q)
      )
      .slice(0, 12);
  }, [localClienti, text]);

  // Verifica se il testo corrisponde esattamente a un cliente
  const exactMatch = useMemo(() => {
    const q = (text || "").trim().toLowerCase();
    if (!q) return null;
    return localClienti.find((c) => (c.nome || "").toLowerCase() === q) || null;
  }, [localClienti, text]);

  const pickCliente = (c) => {
    setText(c.nome);
    onChange?.(c.nome);
    setOpen(false);
  };

  // Mini-dialog di creazione cliente con campi minimi (nome + opzionale "Cerca AI")
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    nome: "", partita_iva: "", stab_codice: "", stab_nome: "",
  });
  const [creating, setCreating] = useState(false);
  const [researching, setResearching] = useState(false);

  const openCreate = () => {
    setCreateForm({
      nome: (text || "").trim(),
      partita_iva: "",
      stab_codice: (defaultStabilimento || "").trim(),
      stab_nome: "",
    });
    setCreateOpen(true);
  };

  // Iter 31u: wizard "sito-first" anche nel mini-dialog di creazione
  // dal Combobox (es. dialog "commesse senza cliente" in Dashboard, dalla
  // lista rapportini). Mostra fino a 3 siti candidati.
  const [siteCandidate, setSiteCandidate] = useState(null);

  const tryResearch = async () => {
    const nome = (createForm.nome || "").trim();
    if (!nome) { toast.error("Inserisci almeno il nome"); return; }
    setResearching(true);
    setSiteCandidate(null);
    try {
      const { data: site } = await api.post(
        "/clienti/research/website",
        { nome },
        { timeout: 35000 }
      );
      const cands = site?.candidates || [];
      if (cands.length > 0) {
        setSiteCandidate({ nome, candidates: cands });
        setResearching(false);
        return;
      }
      // Nessun sito → fallback ricerca generica
      await _tryResearchGeneric(nome);
    } catch (_) {
      await _tryResearchGeneric(nome);
    } finally {
      setResearching(false);
    }
  };

  const applySelectedSite = async (chosen) => {
    if (!siteCandidate || !chosen) return;
    setResearching(true);
    try {
      const { data } = await api.post(
        "/clienti/research/from-website",
        { nome: siteCandidate.nome, sito_web: chosen.sito_web },
        { timeout: 45000 }
      );
      const p = data?.proposed || {};
      setCreateForm((prev) => ({
        ...prev,
        nome: prev.nome || p.nome || "",
        partita_iva: prev.partita_iva || p.partita_iva || "",
      }));
      if (p.partita_iva) toast.success(`P.IVA trovata: ${p.partita_iva}`);
      else toast.info("P.IVA non trovata nel sito. Compila a mano.");
      setSiteCandidate(null);
    } catch (_) {
      toast.error("Estrazione dal sito non riuscita");
    } finally {
      setResearching(false);
    }
  };

  const _tryResearchGeneric = async (nome) => {
    try {
      const { data } = await api.post(
        "/clienti/research",
        { nome },
        { timeout: 45000 }
      );
      const p = data?.proposed || {};
      setCreateForm((prev) => ({
        ...prev,
        nome: prev.nome || p.nome,
        partita_iva: prev.partita_iva || p.partita_iva || "",
      }));
      if (data?.duplicate) {
        toast.warning(`Già presente: ${data.duplicate.nome}`);
      } else if (p.partita_iva) {
        toast.success(`P.IVA trovata: ${p.partita_iva}`);
      } else {
        toast.info("P.IVA non trovata. Inseriscila a mano se la conosci.");
      }
    } catch (e) {
      const code = e?.code === "ECONNABORTED" ? "timeout" : (e?.response?.status || "errore");
      toast.error(`Ricerca AI fallita (${code})`);
    }
  };

  const confirmCreate = async () => {
    const nome = (createForm.nome || "").trim();
    if (!nome) { toast.error("Nome obbligatorio"); return; }
    const piva = (createForm.partita_iva || "")
      .replace(/[^a-zA-Z0-9]/g, "")
      .toUpperCase();
    if (!piva || piva.length < 5) {
      toast.error("P.IVA obbligatoria (italiana o estera, min. 5 caratteri)");
      return;
    }
    // Iter 46: stabilimento (codice EG-XX) obbligatorio anche nel mini-dialog
    const stabCodice = (createForm.stab_codice || "").replace(/\D/g, "");
    if (!stabCodice || stabCodice.length > 2) {
      toast.error(
        "Codice stabilimento obbligatorio: 2 cifre (es. 71 per le commesse EG-71-…)."
      );
      return;
    }
    setCreating(true);
    try {
      const { data: created } = await api.post("/clienti", {
        nome,
        partita_iva: createForm.partita_iva,
        codice_fiscale: "", indirizzo: "", citta: "", cap: "",
        provincia: "", telefono: "", email: "", sito_web: "",
        settore: "", note: "",
        stabilimenti: [
          {
            codice: stabCodice.padStart(2, "0"),
            nome: (createForm.stab_nome || "").trim(),
          },
        ],
      });
      toast.success(`Cliente creato: ${created.nome}`);
      setLocalClienti((prev) => [...prev, created]);
      setCreateOpen(false);
      setText(created.nome);
      onChange?.(created.nome);
      onClienteCreated?.(created);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.duplicate?.nome) {
        // Iter 148: il cliente esiste già → lo selezioniamo al posto di crearne un doppione
        const dup = detail.duplicate;
        toast.info(`"${dup.nome}" esiste già: selezionato`);
        setLocalClienti((prev) => (prev.find((c) => c.id === dup.id) ? prev : [...prev, dup]));
        setCreateOpen(false);
        setText(dup.nome);
        onChange?.(dup.nome);
        onClienteCreated?.(dup);
      } else {
        toast.error(typeof detail === "string" ? detail : "Creazione fallita");
      }
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="relative" ref={wrapperRef}>
      <Input
        data-testid={testId}
        value={text}
        onChange={(e) => {
          const v = e.target.value;
          setText(v);
          onChange?.(v);
          setOpen(true);
          setHighlight(0);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (!open || filtered.length === 0) return;
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlight((h) => Math.min(h + 1, filtered.length - 1));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlight((h) => Math.max(h - 1, 0));
          } else if (e.key === "Enter") {
            e.preventDefault();
            const c = filtered[highlight];
            if (c) pickCliente(c);
          } else if (e.key === "Escape") {
            setOpen(false);
          }
        }}
        placeholder={placeholder}
        className="h-12 mt-1"
        autoComplete="off"
      />
      {open && (
        <div
          data-testid={`${testId}-list`}
          className="absolute z-40 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-72 overflow-auto"
        >
          {filtered.map((c, idx) => (
            <button
              key={c.id}
              type="button"
              data-testid={`${testId}-item-${idx}`}
              onMouseDown={(e) => { e.preventDefault(); pickCliente(c); }}
              className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-slate-100 ${
                idx === highlight ? "bg-slate-100" : ""
              }`}
            >
              <div className="flex-1 min-w-0">
                <div className="font-bold truncate">{c.nome}</div>
                <div className="text-[11px] text-slate-500 truncate">
                  {c.partita_iva ? `P.IVA ${c.partita_iva}` : "P.IVA mancante"}
                  {c.citta && ` · ${c.citta}`}
                </div>
              </div>
              {exactMatch && exactMatch.id === c.id && (
                <Check className="h-4 w-4 text-[#0044FF] shrink-0" />
              )}
            </button>
          ))}
          {/* Bottone "+ Crea nuovo" se non c'è match esatto */}
          {!exactMatch && (text || "").trim().length >= 2 && (
            <button
              type="button"
              data-testid={`${testId}-create`}
              onMouseDown={(e) => { e.preventDefault(); setOpen(false); openCreate(); }}
              className="w-full text-left px-3 py-2 text-sm border-t bg-emerald-50 hover:bg-emerald-100 text-emerald-900 font-bold flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Crea nuovo cliente: &quot;{text}&quot;
            </button>
          )}
          {filtered.length === 0 && (!text || text.trim().length < 2) && (
            <div className="px-3 py-2 text-xs text-slate-500">
              Inizia a digitare per cercare o creare un nuovo cliente…
            </div>
          )}
        </div>
      )}

      {/* Mini-dialog creazione cliente */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md" data-testid={`${testId}-create-dialog`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-emerald-600" />
              Nuovo cliente
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-bold">Nome / Ragione sociale *</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={createForm.nome}
                  onChange={(e) => setCreateForm({ ...createForm, nome: e.target.value })}
                  className="h-11"
                  autoFocus
                  data-testid={`${testId}-create-nome`}
                />
                <Button
                  type="button"
                  onClick={tryResearch}
                  disabled={researching || !createForm.nome.trim()}
                  data-testid={`${testId}-create-research`}
                  className="h-11 bg-emerald-600 hover:bg-emerald-700 whitespace-nowrap"
                >
                  {researching ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Sparkles className="h-4 w-4" />
                  )}
                </Button>
              </div>
              <p className="text-[11px] text-slate-500 mt-1">
                Clicca la stellina per cercare automaticamente la P.IVA online.
              </p>
            </div>

            {/* Iter 31u — Banner 3 siti candidati */}
            {siteCandidate && (
              <div className="border-2 border-purple-300 bg-purple-50 rounded-md p-2 space-y-1.5">
                <div className="text-[11px] uppercase font-bold tracking-wide text-purple-700">
                  Scegli il sito ufficiale
                </div>
                {(siteCandidate.candidates || []).map((c, idx) => (
                  <div
                    key={idx}
                    className="rounded border border-purple-200 bg-white p-1.5"
                  >
                    <a
                      href={
                        c.sito_web.startsWith("http")
                          ? c.sito_web
                          : `https://${c.sito_web}`
                      }
                      target="_blank"
                      rel="noreferrer"
                      className="block font-mono text-xs text-blue-700 hover:underline break-all"
                    >
                      {c.sito_web}
                    </a>
                    <div className="text-[10px] text-slate-600">
                      {c.settore ? `${c.settore} · ` : ""}
                      {c.paese ? `${c.paese} · ` : ""}
                      conf. <strong>{c.confidenza}</strong>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      className="mt-1 h-7 text-xs bg-emerald-600 hover:bg-emerald-700"
                      disabled={researching}
                      onClick={() => applySelectedSite(c)}
                    >
                      Usa questo
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-7 text-xs"
                  onClick={() => setSiteCandidate(null)}
                >
                  Annulla
                </Button>
              </div>
            )}
            <div>
              <Label className="text-xs font-bold">
                Partita IVA <span className="text-red-500">*</span>
              </Label>
              <Input
                value={createForm.partita_iva}
                onChange={(e) =>
                  setCreateForm({ ...createForm, partita_iva: e.target.value })
                }
                placeholder="IT 11 cifre o estera (DE/FR/GB/…)"
                className="h-11 mt-1 font-mono"
                data-testid={`${testId}-create-piva`}
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs font-bold">
                  Codice stabilimento <span className="text-red-500">*</span>
                </Label>
                <Input
                  value={createForm.stab_codice}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, stab_codice: e.target.value })
                  }
                  placeholder="es. 71"
                  maxLength={2}
                  className="h-11 mt-1 font-mono"
                  data-testid={`${testId}-create-stab-codice`}
                />
              </div>
              <div>
                <Label className="text-xs font-bold">Nome stabilimento</Label>
                <Input
                  value={createForm.stab_nome}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, stab_nome: e.target.value })
                  }
                  placeholder="es. Parma (opzionale)"
                  className="h-11 mt-1"
                  data-testid={`${testId}-create-stab-nome`}
                />
              </div>
              <p className="text-[11px] text-slate-500 col-span-2 -mt-1">
                Codice a 2 cifre delle commesse EG-XX-…. Lo stesso cliente può
                avere più stabilimenti, ognuno col proprio codice.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              Annulla
            </Button>
            <Button
              onClick={confirmCreate}
              disabled={creating || !createForm.nome.trim()}
              data-testid={`${testId}-create-confirm`}
              className="bg-[#0044FF] hover:bg-[#0033CC]"
            >
              {creating ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <Plus className="h-4 w-4 mr-1" />
              )}
              Crea e usa
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
